import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import LabelText from "./LabelText";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

const Button = styled.div<{ isOpen?: boolean }>`
  display: flex;
  height: 40px;
  padding: 16px 16px;
  justify-content: center;
  gap: 8px;
  border-radius: 4px;
  .btnText {
    color: var(--Warm-Grey-warm-grey-600, #7c7d79);
    text-align: center;
    font-feature-settings: "clig" off, "liga" off;
    font-family: Aeonik;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 18px; /* 128.571% */
  }
`;
const ListContainer = styled.ul<{ isMaxWidth: boolean }>`
  list-style-type: none;
  padding: 0;
  max-height: ${({ isMaxWidth }) => (isMaxWidth ? "232px" : "")};
  overflow: auto;
  overflow-x: clip;
  &::-webkit-scrollbar-track {
    background-color: ${({ theme }) => theme.scrollbarTrack};
  }
  &::-webkit-scrollbar {
    width: 4px;
    background-color: #f5f5f5;
    position: fixed;
    top: 0px;
    right: 10px;
    bottom: 0px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.scrollbarThumb};
  }
`;

const ListItem = styled.li`
  padding: 10px 12px 8px 12px;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.playerDataBorder};
  background: ${({ theme }) => theme.dropdownBorderColor};
  gap: 4px;
  &:hover {
    background-color: white;
  }
  &.selected {
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.tabTitleColor};
    background: ${({ theme }) => theme.selectedListBackground};
  }
`;

const CheckboxInput = styled.label<{ checked: boolean; indeterminate?: boolean }>`
  display: flex;
  align-items: center;
  position: relative;
  .profile-data.all-checked:after {
    background-color: #313233;
  }
  & > .checkbox + div:before {
    content: "";
    position: absolute;
    top: 0px;
    left: 0px;
    width: 18px;
    height: 18px;
    border-radius: 2px;
    background: ${({ checked, indeterminate }) => (indeterminate ? "#00D1A8" : checked ? "#00D1A8" : "transparent")};
    border: ${({ checked, theme }) => (checked ? "1px solid #00D1A8" : `1px solid ${theme.NumberColor}`)};
  }
  & > .checkbox + div:after {
    border: 3px solid #313233;
  }
`;

interface CustomListProps<T> {
  children: React.ReactNode;
  selectedItems?: T[];
  onSelect?: (item: T | T[] | null, selected: boolean) => void;
  multiSelection?: boolean;
  searchText?: string;
  isCheckBox?: boolean;
  showSelectAllCheckbox?: boolean;
  isDropdown?: boolean;
  isAllSelected?: boolean;
  isGlobalSearch?: boolean;
  isMaxWidth?: boolean;
  isFromSimulation?: boolean
}

function CustomList<T>({
  children,
  selectedItems = [],
  onSelect,
  multiSelection,
  isCheckBox,
  showSelectAllCheckbox,
  isDropdown,
  isAllSelected,
  isGlobalSearch,
  isMaxWidth = true,
  isFromSimulation
}: CustomListProps<T>) {
  const [selected, setSelected] = useState<React.SetStateAction<T[]>>([]);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showAll, setShowAll] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    isDropdown ? setShowAll(false) : setShowAll(true);
  }, [isDropdown]);

  useEffect(() => {
    const isCheckedSelectAll = selectedItems.length > 0 && selectedItems.length === React.Children.count(children);
    setSelectAllChecked(isCheckedSelectAll);
    setSelected(selectedItems);
  }, [selectedItems]);

  useEffect(() => {
    if (selectAllChecked) {
      const allItems = React.Children.map(children, (child) => (child as React.ReactElement<{ item: T }>).props.item) as T[];
      setSelected(allItems);
    } else {
      setSelected(selectedItems);
    }
  }, [selectAllChecked, selectedItems, children]);

  const isSelectAllIndeterminate = selected.length > 0 && selected.length < React.Children.count(children);

  useLayoutEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = isSelectAllIndeterminate;
    }
  }, [isSelectAllIndeterminate, selectedItems]);

  const toggleItemSelection = (item: T) => {
    if (!multiSelection) {
      const newSelected = (selected as T[]).includes(item) ? [] : [item];
      setSelected(newSelected);
      if (onSelect) {
        onSelect(newSelected || null, newSelected.length > 0);
      }
    } else {
      const index = (selected as T[]).findIndex((selectedItem) => selectedItem === item);
      const newSelected = index !== -1 ? (selected as T[]).filter((_, i) => i !== index) : [...(selected as T[]), item];
      setSelected(newSelected);
      const isCheckedSelectAll = newSelected.length > 0 && newSelected.length === React.Children.count(children);
      setSelectAllChecked(isCheckedSelectAll);
      if (onSelect) {
        onSelect(newSelected, !(selected as T[]).includes(item));
      }
    }
  };

  const isSelected = (item: T) => {
    return (selected as T[]).some((selectedItem) => isEqual(selectedItem, item));
  };

  const isEqual = (a: T, b: T): boolean => {
    // For simplicity, let's assume a shallow comparison of properties
    return JSON.stringify(a) === JSON.stringify(b);
  };

  const handleSelectAllChange = () => {
    if (selectAllChecked) {
      setSelected([]);
      setSelectAllChecked(false);
      if (onSelect) {
        onSelect(null, false); // Pass null item and false for deselection
      }
    } else {
      const allItems = React.Children.map(children, (child) => (child as React.ReactElement<{ item: T }>).props.item) as T[];
      setSelected(allItems);
      setSelectAllChecked(true);
      if (onSelect) {
        onSelect(allItems, true); // Pass all selected items and true for selection
      }
    }
    setSelectAllChecked(!selectAllChecked);
  };

  useEffect(() => {
    if (isAllSelected) {
      handleSelectAllChange();
    }
  }, []);

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
    if (!dropdownOpen) setShowAll(false);
  };
  const childrenArray = React.Children.toArray(children);

  const displayChildren = showAll ? childrenArray : childrenArray.slice(0, 5);

  const handleViewAll: any = () => {
    setShowAll(!showAll);
  };

  const listItems = useMemo(() => {
    return displayChildren.map((child, index) => (
      <ListItem
        key={index}
        onClick={isCheckBox ? undefined : () => toggleItemSelection((child as React.ReactElement<{ item: T }>).props.item)}
        className={isSelected((child as React.ReactElement<{ item: T }>).props.item) ? "selected" : ""}
      >
        {isCheckBox ? (
          <CheckboxInput checked={isSelected((child as React.ReactElement<{ item: T }>).props.item)} className="checkbox-input">
            <input
              type="checkbox"
              checked={isSelected((child as React.ReactElement<{ item: T }>).props.item)}
              onChange={() => toggleItemSelection((child as React.ReactElement<{ item: T }>).props.item)}
              className="checkbox"
            />
            {React.cloneElement(child as React.ReactElement<{ item: T }>, { item: (child as React.ReactElement<{ item: T }>).props.item })}
          </CheckboxInput>
        ) : (
          React.cloneElement(child as React.ReactElement<{ item: T }>, { item: (child as React.ReactElement<{ item: T }>).props.item })
        )}
      </ListItem>
    ));
  }, [children, isCheckBox, isSelected, toggleItemSelection]);

  return (
    <ListContainer isMaxWidth={isMaxWidth!} className={`custom-scrollbar ${isGlobalSearch ? "global-search" : ""}`}  style={{maxHeight: isFromSimulation ? '700px' : isMaxWidth ? '232px' : ''}}>
      {multiSelection && showSelectAllCheckbox && (
        <>
          <ListItem>
            <CheckboxInput checked={selectAllChecked} indeterminate={isSelectAllIndeterminate} className="checkbox-input">
              <input type="checkbox" checked={selectAllChecked} onChange={handleSelectAllChange} ref={inputRef} className="checkbox" />
              <div className={`profile-data ${isSelectAllIndeterminate ? "all-checked" : ""}`}>
                <LabelText>All related roles</LabelText>
                <LabelText className="player-team-text">All selected zones</LabelText>
              </div>
            </CheckboxInput>
            {isDropdown && (dropdownOpen ? <RemoveIcon onClick={toggleDropdown} /> : <AddIcon onClick={toggleDropdown} />)}
          </ListItem>
        </>
      )}
      {(!isDropdown || (isDropdown && dropdownOpen)) && listItems}
      {childrenArray.length > 5 && !showAll && isDropdown && dropdownOpen && (
        <Button onClick={handleViewAll}>
          <span className="btnText">View All</span>
        </Button>
      )}
      {/* {isDropdown && dropdownOpen && (
                React.Children.map(children, (child, index) => (
                    <ListItem
                        key={index}
                        onClick={isCheckBox ? undefined : () => toggleItemSelection((child as React.ReactElement<{ item: T }>).props.item)}
                        className={isSelected((child as React.ReactElement<{ item: T }>).props.item) ? 'selected' : ''}
                    >
                        {isCheckBox ?
                            <CheckboxInput checked={isSelected((child as React.ReactElement<{ item: T }>).props.item)} className='checkbox-input'>
                                <input
                                    type="checkbox"
                                    checked={isSelected((child as React.ReactElement<{ item: T }>).props.item)}
                                    onChange={() => toggleItemSelection((child as React.ReactElement<{ item: T }>).props.item)}
                                    className="checkbox"
                                />
                                {/* <LabelText className={"label-text"}>{option}</LabelText>                      
                                {React.cloneElement(child as React.ReactElement<{ item: T }>, { item: (child as React.ReactElement<{ item: T }>).props.item })}
                            </CheckboxInput>
                            : React.cloneElement(child as React.ReactElement<{ item: T }>, { item: (child as React.ReactElement<{ item: T }>).props.item })
                        }
                    </ListItem>
                ))
            )}
            {!isDropdown && (
                React.Children.map(children, (child, index) => (
                    <ListItem
                        key={index}
                        onClick={isCheckBox ? undefined : () => toggleItemSelection((child as React.ReactElement<{ item: T }>).props.item)}
                        className={isSelected((child as React.ReactElement<{ item: T }>).props.item) ? 'selected' : ''}
                    >
                        {isCheckBox ?
                            <CheckboxInput checked={isSelected((child as React.ReactElement<{ item: T }>).props.item)} className='checkbox-input'>
                                <input
                                    type="checkbox"
                                    checked={isSelected((child as React.ReactElement<{ item: T }>).props.item)}
                                    onChange={() => toggleItemSelection((child as React.ReactElement<{ item: T }>).props.item)}
                                    className="checkbox"
                                />
                                {/* <LabelText className={"label-text"}>{option}</LabelText>                      
                                {React.cloneElement(child as React.ReactElement<{ item: T }>, { item: (child as React.ReactElement<{ item: T }>).props.item })}
                            </CheckboxInput>
                            : React.cloneElement(child as React.ReactElement<{ item: T }>, { item: (child as React.ReactElement<{ item: T }>).props.item })
                        }
                    </ListItem>
                ))
            )} */}
    </ListContainer>
  );
}

export default CustomList;
